import { Skeleton } from '@portals-platform/ui';
import { useMemo } from 'react';
import {
  isAvailableForRenewal,
  isCertificateEditable,
  useCertificate,
  useRequestCertificateRenewal,
} from '../../data-access/index.js';
import { toCertificateDetails } from '../../data-access/mappers/certificate-mappers.js';
import { CertificateDetailsView } from './certificate-details-view.js';
import { CertificateLoadError } from './certificate-load-error.js';

interface CertificateDetailsProps {
  certificateId: string;
  onBack: () => void;
  onEdit: () => void;
}

export function CertificateDetails({ certificateId, onBack, onEdit }: CertificateDetailsProps) {
  const { certificate: response, isLoading, isError, error } = useCertificate(certificateId);
  const certificate = useMemo(
    () => (response ? toCertificateDetails(response) : undefined),
    [response],
  );
  const renewal = useRequestCertificateRenewal();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !certificate) {
    return <CertificateLoadError error={error} onBack={onBack} />;
  }

  return (
    <CertificateDetailsView
      certificate={certificate}
      canEdit={isCertificateEditable(certificate)}
      canRequestRenewal={isAvailableForRenewal(certificate)}
      isRequestingRenewal={renewal.isPending}
      renewalRequested={renewal.isSuccess}
      renewalError={renewal.error}
      onBack={onBack}
      onEdit={onEdit}
      onRequestRenewal={() => renewal.mutate(certificate.id)}
    />
  );
}
